import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, getToken } from '../App';
import AuthModal from './AuthModal';
import { fmtNum } from '../utils/numerology';

function renderText(text) {
  if (!text) return null;
  return text.split('\n\n').filter(p => p.trim()).map((p, i) => <p key={i}>{p.trim()}</p>);
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

export default function SavedReadings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const token = getToken();
    axios.get(`${API}/readings/saved`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setReadings(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="saved-page">
        <div className="saved-empty" data-testid="saved-not-logged-in">
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 300, marginBottom: 12 }}>Your saved readings</div>
          <p style={{ color: 'var(--muted)', marginBottom: 28 }}>Sign in to access your saved blueprint readings.</p>
          <button className="tool-btn" onClick={() => setShowAuth(true)} data-testid="signin-to-view-btn">Sign in</button>
        </div>
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => setShowAuth(false)} />}
      </div>
    );
  }

  return (
    <div className="saved-page" data-testid="saved-readings-page">
      <div style={{ marginBottom: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300, marginBottom: 4 }}>
            Saved readings
          </div>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>Hello, {user.name}</div>
        </div>
        <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="new-reading-from-saved-btn">
          New reading →
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--muted)' }} data-testid="saved-loading">
          <div className="loading-dots" style={{ justifyContent: 'center' }}>
            <div className="dot" /><div className="dot" /><div className="dot" />
          </div>
        </div>
      )}

      {!loading && readings.length === 0 && (
        <div className="saved-empty" data-testid="saved-empty">
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 300, marginBottom: 12 }}>
            No saved readings yet
          </div>
          <p style={{ color: 'var(--muted)', marginBottom: 24 }}>Generate a reading and save it to see it here.</p>
          <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="get-first-reading-btn">
            Get your first reading →
          </button>
        </div>
      )}

      {!loading && readings.map((r, i) => (
        <div
          key={r.session_id || i}
          className="saved-card"
          onClick={() => setExpanded(expanded === i ? null : i)}
          data-testid={`saved-card-${i}`}
        >
          <div className="saved-card-header">
            <div>
              <div className="saved-card-name">{r.name}</div>
              <div className="saved-card-stage">{r.stage_label} · {r.mode === 'child' ? 'Child reading' : 'Personal reading'}</div>
            </div>
            <div className="saved-card-date">{formatDate(r.created_at)}</div>
          </div>

          {r.numbers && (
            <div className="saved-card-numbers">
              <span className="saved-num-chip">Life Path {fmtNum(r.numbers.life_path)}</span>
              <span className="saved-num-chip">Expression {fmtNum(r.numbers.expression)}</span>
              <span className="saved-num-chip">Soul Urge {fmtNum(r.numbers.soul_urge)}</span>
              <span className="saved-num-chip">{r.numbers.star_sign}</span>
            </div>
          )}

          {expanded === i && r.reading && (
            <div className="saved-reading-text" data-testid={`saved-reading-text-${i}`}>
              {renderText(r.reading)}
            </div>
          )}

          <div style={{ fontSize: 12, color: 'var(--blue)', marginTop: 8 }}>
            {expanded === i ? '▲ Hide reading' : '▼ View reading'}
          </div>
        </div>
      ))}

      {/* DELETE MY ACCOUNT — GDPR / Australian Privacy Act right-to-be-forgotten */}
      <div className="danger-zone" data-testid="danger-zone">
        <div className="danger-zone-label">Privacy &amp; account</div>
        <div className="danger-zone-row">
          <div>
            <div className="danger-zone-title">Delete my account &amp; data</div>
            <div className="danger-zone-sub">
              Permanently removes your account, every saved reading, family member, and email record.
              This cannot be undone.
            </div>
          </div>
          <button
            type="button"
            className="danger-zone-btn"
            onClick={() => { setShowDeleteModal(true); setDeleteConfirm(''); setDeleteError(''); }}
            data-testid="open-delete-account-btn"
          >
            Delete my account
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" style={{ zIndex: 260 }} data-testid="delete-account-modal">
          <div className="modal-box" style={{ textAlign: 'left' }}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ marginBottom: 8 }}>Delete your account?</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 18 }}>
              We&rsquo;ll permanently remove <strong>{user.name}&rsquo;s</strong> account ({user.email}),
              every reading you&rsquo;ve saved, every family member you&rsquo;ve added, and your email
              from our list. This cannot be undone.
            </p>
            <p style={{ fontSize: 13, color: 'var(--body)', marginBottom: 8 }}>
              To confirm, type <strong>DELETE</strong> below.
            </p>
            <input
              className="field-input"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder="Type DELETE to confirm"
              data-testid="delete-confirm-input"
              autoFocus
            />
            {deleteError && (
              <div style={{ background: 'rgba(220,38,38,0.08)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: '#dc2626', marginTop: 12 }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{ flex: 1, minWidth: 140, background: 'none', border: '1.5px solid var(--border-strong)', borderRadius: 100, padding: '12px 24px', fontSize: 13, color: 'var(--body)', cursor: 'pointer', fontFamily: 'inherit' }}
                data-testid="cancel-delete-btn"
              >
                Keep my account
              </button>
              <button
                type="button"
                disabled={deleting || deleteConfirm.trim() !== 'DELETE'}
                onClick={async () => {
                  setDeleting(true);
                  setDeleteError('');
                  try {
                    const token = getToken();
                    await axios.delete(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
                    logout();
                    navigate('/');
                  } catch (err) {
                    const detail = err?.response?.data?.detail;
                    setDeleteError(detail || "We couldn't delete the account. Please try again or email hello@littlebluenumbers.com.");
                    setDeleting(false);
                  }
                }}
                style={{ flex: 1, minWidth: 140, background: deleteConfirm.trim() === 'DELETE' ? '#dc2626' : '#cbb6b6', color: 'white', border: 'none', borderRadius: 100, padding: '12px 24px', fontSize: 13, fontWeight: 500, cursor: deleteConfirm.trim() === 'DELETE' ? 'pointer' : 'not-allowed', fontFamily: 'inherit', transition: 'all 0.18s' }}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting…' : 'Yes, delete everything'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
